<script setup lang="ts">
import { computed } from 'vue';

import { useAuthStore } from '@/entities/auth';
import { BaseModal } from '@/shared/ui';

import { useOrderFiscalization } from '../model/use-order-fiscalization';
import OrderFiscalizationProgress from './OrderFiscalizationProgress.vue';

/** Отмена платежа из карточки: тот же экран, что в оплате, поверх восстановленной операции. */
const props = defineProps<{ orderId: string }>();
const emit = defineEmits<{ changed: []; close: [] }>();

const auth = useAuthStore();
const fiscalization = useOrderFiscalization({
  locationId: computed(() => auth.currentLocation?.id),
  organizationId: computed(() => auth.currentOrganization?.id),
});
const state = fiscalization.state;

// Диалог монтируется на одно действие: операцию восстанавливаем сразу.
void fiscalization.recover(props.orderId);

async function run(action: () => Promise<unknown>): Promise<void> {
  await action();
  emit('changed');
}
</script>

<template>
  <BaseModal
    show
    :loading="state.phase === 'idle'"
    :persistent="fiscalization.isBusy.value"
    size="md"
    @close="emit('close')"
  >
    <template #title>Отмена платежа</template>
    <OrderFiscalizationProgress
      v-if="state.phase !== 'idle'"
      :amount="state.amount ?? 0"
      :can-cancel-payment="state.canCancelPayment"
      :can-retry-receipt="state.canRetryReceipt"
      :can-resolve-receipt="state.canResolveReceipt"
      :cash-to-return="state.cashToReturn"
      :receipt-question="state.receiptQuestion"
      :message="state.message"
      :phase="state.phase"
      :shift-action="state.shiftAction"
      :terminal-name="state.terminalName"
      :technical-details="state.technicalDetails"
      @cancel-payment="run(fiscalization.cancelUnconfirmedPayment)"
      @cancel-without-check="run(fiscalization.cancelWithoutCheck)"
      @retry-finalize="run(fiscalization.retryFinalize)"
      @retry-receipt="run(fiscalization.retryReceipt)"
      @resolve-receipt="run(fiscalization.resolveReceipt)"
      @answer-receipt-printed="run(() => fiscalization.answerReceiptPrinted($event))"
      @fix-shift="fiscalization.fixShift"
    />
  </BaseModal>
</template>
